import "./messages/MessagesView.css";
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MessagesInbox, type InboxThread, type InboxFolder } from "../components/MessagesInbox";
import { DisputeConversation } from "../components/DisputeConversation";
import { profileKey } from "../lib/profileSession";
import { refreshFamily, requireRefreshArray, useRefreshOwner } from "../lib/useRefreshOwner";

export default function DisputeMessages() {
  const { user, loading: authLoading } = useAuth();
  const [, params] = useRoute("/messages/disputes/:id");
  const [, navigate] = useLocation();
  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const id = params?.id;
  const identity = `${profileKey(user)}:dispute-inbox`;
  const refresh = useRefreshOwner(identity, signal => [
    refreshFamily(async () => {
      const response = await api.listThreads("active", signal);
      requireRefreshArray(response?.threads);
      if (!Number.isFinite(response.archivedCount)) throw new Error("Missing chat counts.");
      return response;
    }, inbox => { setArchivedCount(inbox.archivedCount); setUnreadCount(inbox.threads.filter(thread => thread.unread).length); },
    () => { setArchivedCount(0); setUnreadCount(0); }),
    refreshFamily(() => api.myDisputes(signal), disputes => setThreads(disputes.map(dispute => ({
      id: dispute.id, otherName: dispute.counterpartyName, subject: dispute.contextLabel,
      lastMessage: `${dispute.resolution === "withdrawn" ? "Withdrawn · Agreement continues" : dispute.resolution === "continue" ? "Resolved · Agreement continues" : dispute.status === "unresolved" ? "Unresolved" : "Dispute"} · ${dispute.contextLabel || dispute.reason}`,
      lastAt: dispute.updatedAt, unread: dispute.unread,
      businessName: dispute.openedByCompanyId || dispute.againstCompanyId ? "Business agreement" : null,
    })).sort((a, b) => b.lastAt.localeCompare(a.lastAt))), () => setThreads([])),
  ], { enabled: !!user, primaryFamily: { index: 1, loadError: "Could not load disputes. Please try again." } });
  const loading = !refresh.loaded;
  const error = refresh.error;
  useEffect(() => {
    if (!user) return;
    const poll = () => { if (document.visibilityState === "visible") void refresh.reload(true); };
    const timer = setInterval(poll, 30000);
    window.addEventListener("dispute-read", poll); window.addEventListener("focus", poll);
    document.addEventListener("visibilitychange", poll);
    return () => { clearInterval(timer); window.removeEventListener("dispute-read", poll); window.removeEventListener("focus", poll); document.removeEventListener("visibilitychange", poll); };
  }, [identity]);
  function selectFolder(folder: InboxFolder) {
    if (folder !== "disputes") navigate(folder === "archived" ? "/messages?folder=archived" : "/messages");
  }
  if (authLoading) return <p role="status" className="p-6 text-sm text-mut">Loading disputes…</p>;
  if (!user) return <div className="p-6 text-sm text-mut">Sign in to view your disputes. <Link href={`/login?returnTo=${encodeURIComponent(id ? `/messages/disputes/${id}` : "/messages/disputes")}`} className="font-semibold text-navy underline">Sign in</Link></div>;
  return <div className={`messages-frame min-h-0 bg-white ${id ? "fixed inset-0 z-40 lg:static" : ""}`}>
    <div className="grid h-full min-h-0 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
      <div className={`h-full min-h-0 ${id ? "hidden lg:contents" : "grid lg:contents"}`}>
        <MessagesInbox threads={threads} folder="disputes" activeId={id || null} archivedCount={archivedCount}
          unreadCount={unreadCount} disputeUnreadCount={threads.filter(thread => thread.unread).length}
          loading={loading} error={error} onFolder={selectFolder} onRetry={() => void refresh.reload()}
          refresh={!id ? { identity, disabled: loading, onRefresh: refresh.onRefresh } : undefined}
          onOpen={caseId => navigate(`/messages/disputes/${encodeURIComponent(caseId)}`)} />
      </div>
      {id ? <DisputeConversation id={id} onBack={() => navigate("/messages/disputes")} desktopBack={false} />
        : <section aria-label="Dispute workspace" className="hidden min-h-0 min-w-0 place-items-center p-8 text-center lg:grid">
          <div><span className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-full bg-navy-50"><ShieldCheck aria-hidden="true" className="h-9 w-9 text-navy" /></span>
            <h2 className="text-xl font-bold text-navy">Your disputes</h2>
            <p className="mt-2 max-w-xs text-sm text-mut">Discussions start when a dispute is filed. Select a case to read messages and review proposed terms.</p>
          </div>
        </section>}
    </div>
  </div>;
}
